import {Observable} from 'rxjs';
import Axios from 'axios-observable';
import {catchError, map, retry} from 'rxjs/operators';
import {axiosInstance, axiosInstanceAuthenticated} from '@/api/axios.config.api';
import {BaseModel} from '@/models/base.model';

export enum RequireAuthApi {
  ALL, CREATE, SHOW, UPDATE, DESTROY,
}

export interface PaginatedResponse<T extends BaseModel> {
  current_page: number;
  data: T[];
  first_page_url: string;
  from: string;
  last_page: number;
  last_page_url: string;
  next_page_url: string;
  path: string;
  per_page: number;
  prev_page: number;
  prev_page_url: string;
  to: string;
  total: number;
}

export interface PaginatedParams {
  page?: number;

  [key: string]: string | number | undefined
}

export abstract class BaseApi<T extends BaseModel> {

  protected constructor(protected url: string, protected protectionTo?: RequireAuthApi[]) {
  }

  all(params?: PaginatedParams): Observable<T[]> {
    return this.axiosWithPermissionsCheck(RequireAuthApi.ALL)
      .get<T[]>(this.url, {params})
      .pipe(map((response) => response.data))
  }

  allPaginated(params?: PaginatedParams): Observable<PaginatedResponse<T>> {
    return this.axiosWithPermissionsCheck(RequireAuthApi.ALL)
      .get<PaginatedResponse<T>>(this.url, {params})
      .pipe(map((response) => response.data))
  }

  findById(id: number): Observable<T> {
    return this.axiosWithPermissionsCheck(RequireAuthApi.SHOW)
      .get<T>(`${this.url}/${id}`)
      .pipe(map((response) => response.data))
  }

  create(data: T): Observable<T> {
    return this.axiosWithPermissionsCheck(RequireAuthApi.CREATE)
      .post<T>(this.url, data)
      .pipe(map((response) => response.data))
  }

  update(data: T): Observable<T> {
    return this.axiosWithPermissionsCheck(RequireAuthApi.UPDATE)
      .put<T>(`${this.url}/${data.id}`, data)
      .pipe(map((response) => response.data))
  }

  destroy(id: number): Observable<any> {
    return this.axiosWithPermissionsCheck(RequireAuthApi.DESTROY)
      .delete<T>(`${this.url}/${id}`)
      .pipe(map((response) => response.data))
  }

  private axiosWithPermissionsCheck(protection: RequireAuthApi): Axios {
    if (this.protectionTo?.includes(protection)) {
      return axiosInstanceAuthenticated()
    }
    return axiosInstance()
  }

}
